import React, { useState, useRef, useEffect } from "react";
import editIcon from "assets/themeIcons/edit-icon.svg";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import { uploadImageToGhl } from "hook/services";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const News = ({ data, onLoad, agency }) => {
  const currentItems = data || [];
  const AppService = useAppServices();

  const [selectedNews, setSelectedNews] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  const handleOpenModal = (news) => {
    setSelectedNews(news);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedNews(null);
    setIsModalOpen(false);
  };

  // Initialize Quill
  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        if (quillRef.current && !quillInstance.current) {
          quillInstance.current = new Quill(quillRef.current, {
            theme: "snow",
          });
        }
        if (selectedNews && quillInstance.current) {
          quillInstance.current.root.innerHTML = selectedNews.description || "";
        }
      }, 100);
    }

    return () => {
      if (!isModalOpen && quillInstance.current) {
        quillInstance.current = null;
      }
    };
  }, [isModalOpen]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageUploading(true);
    try {
      const uploaded = await uploadImageToGhl(file, agency);

      if (uploaded?.data) {
        setSelectedNews({ ...selectedNews, image: uploaded.data });
        toast.success("Image uploaded successfully!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const descriptionHTML = quillInstance.current.root.innerHTML;

      const payload = {
        _id: selectedNews?._id,
        title: selectedNews?.title,
        description: descriptionHTML,
        image: selectedNews?.image || "",
      };

      await AppService.newsFeed.Update({ payload });

      toast.success("News updated successfully");
      handleCloseModal();
      onLoad();
    } catch (err) {
      console.error("Update Error:", err);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const tableTHStyles = {
    fontFamily: "Poppins",
    fontWeight: 600,
  };

  return (
    <>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table
          className="table"
          style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}
        >
          <thead>
            <tr style={{ background: "#F7F7F7", borderRadius: "18px" }}>
              <th className="p-20-px" style={tableTHStyles}>Image</th>
              <th className="p-20-px" style={tableTHStyles}>Title</th>
              <th className="p-20-px" style={tableTHStyles}>Business</th>
              <th className="p-20-px" style={tableTHStyles}>Description</th>
              <th className="p-20-px" style={tableTHStyles}>Date</th>
              <th className="p-20-px" style={tableTHStyles}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((item, idx) => (
                <tr key={idx}>
                  <td className="p-20-px">
                    {item?.image ? (
                      <img
                        src={item.image}
                        alt="blog"
                        style={{ width: 60, height: 60, borderRadius: 6, objectFit: "cover" }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>

                  <td className="p-20-px">{item?.title}</td>
                  <td className="p-20-px">{item?.account_id?.first_name}</td>
                  <td className="p-20-px">
                    <div dangerouslySetInnerHTML={{ __html: item?.description }} />
                  </td>

                  <td className="p-20-px">
                    {new Date(item?.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>

                  <td className="p-20-px">
                    <img
                      width={20}
                      src={editIcon}
                      onClick={() => handleOpenModal(item)}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No news found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------------------- MODAL ---------------------- */}
      {isModalOpen && (
        <div style={modalWrapper}>
          <div style={modalBox}>
            <h3>Edit News</h3>

            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={selectedNews?.title || ""}
              onChange={(e) =>
                setSelectedNews({ ...selectedNews, title: e.target.value })
              }
              style={inputStyle}
            />

            {/* Description (with Quill Editor) */}
            <label style={labelStyle}>Description</label>
            <div
              ref={quillRef}
              className="border border-gray-300 rounded-md"
              style={{ height: 200, overflow: "auto" }}
            ></div>

            <label style={labelStyle}>Image</label>
            <input type="file" accept="image/*" onChange={handleImageChange} />

            <div style={buttonRow}>
              <button onClick={handleCloseModal} style={cancelBtn}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading || imageUploading}
                style={saveBtn}
              >
                {loading || imageUploading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ===== STYLES =====
const modalWrapper = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalBox = {
  background: "#fff",
  padding: "30px",
  borderRadius: "10px",
  width: "500px",
  maxWidth: "90%",
  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  marginTop: "8px",
  borderRadius: "8px",
  border: "1px solid #ddd",
};

const labelStyle = {
  fontWeight: 600,
  marginTop: "20px",
  display: "block",
};

const buttonRow = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "25px",
};

const cancelBtn = {
  padding: "8px 16px",
  background: "#ccc",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
};

const saveBtn = {
  padding: "8px 16px",
  background: "#007bff",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  cursor: "pointer",
};

export default News;
